import { useRef } from "react";
import {
  PerformanceProDateNavigation,
  usePerformanceProDateNavigation,
} from "../../components/PerformancePro/usePerformanceProDateNavigation";
import Toolbar from "../../components/UI/Toolbar/Toolbar";
import { ToolbarDateNavigation } from "../../components/UI/Toolbar/ToolbarDateNavigation";
import { ToolbarDatePeriodButtonGroup } from "../../components/UI/Toolbar/ToolbarDatePeriodButtonGroup";
import { ToolbarEggTypeSelect } from "../../components/UI/Toolbar/ToolbarEggTypeSelect";
import { useIsMobileSize } from "../../globalHooks/responsiveHooks";
import {
  usePerformanceProFilterOptions,
  usePerformanceProFilterOptionsActions,
} from "../../store/GlobalContext";
import { DatePeriod } from "../../utility/DateUtility";
import {
  selectedDatePeriodKey,
  selectedEggTypeKey,
} from "../../utility/LocalStorageKeys";
import {
  PerformanceProCompactNavigation,
  useIsElementScrolledOutOfView,
} from "./PerformanceProCompactNavigation";

export interface PerformanceProToolbarContainerProps {
  enableEggTypeSelect: boolean;
  enableDatePeriod: boolean;
}

export function PerformanceProToolbarContainer({
  enableEggTypeSelect,
  enableDatePeriod,
}: PerformanceProToolbarContainerProps) {
  const performanceProFilterOptions = usePerformanceProFilterOptions();
  const dispatch = usePerformanceProFilterOptionsActions();
  const dateNavigation = usePerformanceProDateNavigation(enableDatePeriod);

  const handleDatePeriodChange = (newDatePeriod: DatePeriod) => {
    localStorage.setItem(selectedDatePeriodKey, newDatePeriod.toString());
    dispatch({
      ...performanceProFilterOptions,
      selectedDatePeriod: newDatePeriod,
    });
  };

  const handleEggTypeChange = (newEggType: number) => {
    localStorage.setItem(selectedEggTypeKey, newEggType.toString());
    dispatch({
      ...performanceProFilterOptions,
      selectedEggType: newEggType,
    });
  };

  return (
    <PerformanceProToolbarPresentation
      enableEggTypeSelect={enableEggTypeSelect}
      enableDatePeriod={enableDatePeriod}
      dateNavigation={dateNavigation}
      selectedDatePeriod={performanceProFilterOptions.selectedDatePeriod}
      selectedEggType={performanceProFilterOptions.selectedEggType}
      onDatePeriodChange={handleDatePeriodChange}
      onEggTypeChange={handleEggTypeChange}
    />
  );
}

export interface PerformanceProToolbarPresentationProps {
  enableEggTypeSelect: boolean;
  enableDatePeriod: boolean;
  dateNavigation: PerformanceProDateNavigation;
  selectedDatePeriod: DatePeriod;
  selectedEggType: number;
  onDatePeriodChange: (newDatePeriod: DatePeriod) => void;
  onEggTypeChange: (newEggType: number) => void;
}

export function PerformanceProToolbarPresentation({
  enableEggTypeSelect,
  enableDatePeriod,
  dateNavigation,
  selectedDatePeriod,
  selectedEggType,
  onDatePeriodChange,
  onEggTypeChange,
}: PerformanceProToolbarPresentationProps) {
  const isMobileSize = useIsMobileSize();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const isToolbarOutOfView = useIsElementScrolledOutOfView(
    toolbarRef,
    !isMobileSize
  );

  return (
    <>
      <div ref={toolbarRef}>
        <Toolbar>
          <ToolbarDateNavigation {...dateNavigation} />
          {enableDatePeriod && (
            <ToolbarDatePeriodButtonGroup
              selectedDatePeriod={selectedDatePeriod}
              onDatePeriodChange={onDatePeriodChange}
            />
          )}
          {enableEggTypeSelect && (
            <ToolbarEggTypeSelect
              selectedEggType={selectedEggType}
              onEggTypeChange={onEggTypeChange}
            />
          )}
        </Toolbar>
      </div>
      <PerformanceProCompactNavigation
        {...dateNavigation}
        isMobileSize={isMobileSize}
        visible={isToolbarOutOfView}
      />
    </>
  );
}
